import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/components/common/Logo";
import {
  LayoutDashboard,
  Building2,
  Users,
  ShieldCheck,
  Layers3,
  CreditCard,
  Boxes,
  Plug,
  LifeBuoy,
  ScrollText,
  Settings,
  ChefHat,
  ChevronDown,
  ChevronRight,
  Utensils,
  Receipt,
  PlusCircle,
  CalendarCheck,
  ShoppingBag,
  ContactRound,
  FileText,
  Wallet,
  BookOpen,
  Truck,
  Sprout,
  Warehouse,
  ListChecks,
  CalendarClock,
  TrendingUp,
  Crown,
  Activity,
  FileSearch,
  Brain,
  Wallet as WalletIcon,
  BedDouble,
  CalendarRange,
  LogIn,
  LogOut,
  Brush,
  Wallet as HotelWalletIcon,
  Hotel as HotelIcon,
  FileBarChart,
  Users as UsersIcon,
  FileText as QuoteIcon,
  History as HistoryIcon,
  Moon as MoonIcon,
  Shield as ShieldIcon,
  Tag,
  Sparkles,
  BarChart3,
  Bell,
  Database,
  Rocket,
  Clock,
  ShoppingCart,
  ClipboardCheck,
  Printer,
  FileSpreadsheet,
  Wine,
  Gauge,
  CalendarCheck2,
  Target,
  Mail,
  MessageSquare,
  KeyRound,
  Inbox,
  Zap,
  Radio,
  Smartphone,
  QrCode,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useAccessMatrix } from "@/context/AccessMatrixContext";
import { usePermissions } from "@/lib/usePermissions";
import { MODULE_NAV_GROUPS, planLabel, roleLabel, moduleLabel } from "@/lib/constants";
import { PLACEHOLDER_MODULES, getModuleTier } from "@/lib/moduleTiers";
import ModuleBadge from "@/components/common/ModuleBadge";
import { getPlan, hasFeature } from "@/lib/plansConfig";
import { getModule } from "@/config/modulesConfig";
import { getModuleStatus, getUpgradeTarget } from "@/lib/featureAccess";

function NavItem({ to, label, icon: Icon, testId, badge }) {
  return (
    <NavLink
      to={to}
      end={to === "/dashboard"}
      data-testid={testId}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-[#E0F2F1] text-[#102A43]"
            : "cx-nav-item text-[#475569] hover:bg-[#F2F4F2] hover:text-[#0f172a]"
        }`
      }
    >
      <Icon className="w-4 h-4 shrink-0" />
      <span className="truncate">{label}</span>
      {badge && (
        <span
          className="ml-auto px-1.5 py-0.5 rounded text-[9px] uppercase font-bold tracking-wider bg-[#FFF3DD] text-[#A36B0F]"
          data-testid={`${testId}-badge`}
        >
          {badge}
        </span>
      )}
    </NavLink>
  );
}

function ModuleGroup({ group, enabledModules, t, tenantPlanId, perms }) {
  // Show every module in the group, regardless of inclusion. Modules NOT
  // included in the current plan render with an UPGRADE/ENTERPRISE badge and
  // route to /locked/:moduleId instead of the real route. No "presto" label.
  //
  // We only filter out modules that have NO catalog entry at all (defensive).
  // RBAC: hide modules whose permission the role doesn't grant (Phase RBAC
  // hardening). Map kept minimal — only items with a clear permission match.
  const MODULE_PERM_MAP = {
    payments: "manage_payments",
    reservations: "manage_reservations",
    reports: "view_reports",
  };
  const visibleItems = group.items.filter((id) => {
    const m = getModule(id);
    const placeholder = PLACEHOLDER_MODULES[id];
    if (!(m || placeholder)) return false;
    const requiredPerm = MODULE_PERM_MAP[id];
    if (requiredPerm && perms && !perms.has(requiredPerm)) return false;
    return true;
  });
  const [open, setOpen] = useState(true);
  if (visibleItems.length === 0) return null;
  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="cx-nav-eyebrow w-full flex items-center justify-between px-3 py-1 label-eyebrow text-[#475569] hover:text-[#0f172a] transition-colors"
        data-testid={`nav-group-${group.id}`}
      >
        <span>{t(`nav_groups.${group.id}`)}</span>
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="mt-1 space-y-0.5">
          {visibleItems.map((id) => {
            const m = getModule(id);
            // Status from feature access: active | upgrade | enterprise.
            const status = m ? getModuleStatus(tenantPlanId, id) : "active";
            const isLocked = status === "upgrade" || status === "enterprise";
            const target = m?.route || `/modules/${id}`;
            const to = isLocked ? `/locked/${id}` : target;
            const tierKey =
              status === "enterprise" ? "enterprise" : status === "upgrade" ? "pro" : null;
            return (
              <NavLink
                key={id}
                to={to}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-[#E0F2F1] text-[#102A43]"
                      : isLocked
                        ? "cx-nav-item cx-nav-locked text-[#94A3B8] hover:bg-[#F8FAFC] hover:text-[#475569]"
                        : "cx-nav-item text-[#475569] hover:bg-[#F2F4F2] hover:text-[#0f172a]"
                  }`
                }
                data-testid={`nav-module-${id}`}
                data-status={status}
                title={m?.label || moduleLabel(t, id)}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    isLocked ? "bg-[#CBD5E1]" : "bg-[#102A43]/70"
                  }`}
                />
                <span className="truncate flex-1 min-w-0">{m?.label || moduleLabel(t, id)}</span>
                {tierKey && <ModuleBadge tier={tierKey} size="xs" className="ml-auto shrink-0" />}
                {!tierKey && status === "active" && PLACEHOLDER_MODULES[id] && (
                  <ModuleBadge tier="beta" size="xs" className="ml-auto shrink-0" />
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

function planSectionLabel(tenant, family /* "hotel"|"restaurant"|"combo" */) {
  // SSOT: derive section label from tenant.plan_code / plan_name / product_family
  const code = (tenant?.plan_code || "").toLowerCase();
  const family_t = (tenant?.product_family || "").toLowerCase();
  const tier = (tenant?.plan_tier || "").toLowerCase();
  if (family_t === "combo" || code === "combo_premium") return "COMBO PREMIUM";
  if (family === "hotel") {
    if (code === "hotel_professional" || tier === "professional") return "HOTEL PROFESSIONAL";
    if (code === "hotel_starter" || tier === "starter") return "HOTEL STARTER";
    return tenant?.plan_name ? tenant.plan_name.toUpperCase() : "HOTEL";
  }
  if (family === "restaurant") {
    if (code === "professional_restaurant" || tier === "professional") return "RESTAURANT PROFESSIONAL";
    if (code === "starter_restaurant" || tier === "starter") return "RESTAURANT STARTER";
    return tenant?.plan_name ? tenant.plan_name.toUpperCase() : "RESTAURANT";
  }
  return tenant?.plan_name ? tenant.plan_name.toUpperCase() : "HOTEL";
}

function HotelNav({ enabledModules, tenant }) {
  const hasHotel = enabledModules.includes("hotel_core");
  const { permissions: perms } = usePermissions();
  if (!hasHotel) return null;
  const sectionLabel = planSectionLabel(tenant, "hotel");
  const links = [
    { to: "/hotel/today", label: "Centro Operativo", icon: LayoutDashboard, testId: "nav-hotel-today" },
    { to: "/hotel/dashboard", label: "Dashboard KPI", icon: LayoutDashboard, testId: "nav-hotel-dashboard" },
    { to: "/hotel/rooms", label: "Camere", icon: BedDouble, testId: "nav-hotel-rooms", perm: "manage_room_status" },
    { to: "/hotel/room-types", label: "Tipologie Camere", icon: HotelIcon, testId: "nav-hotel-room-types", perm: "manage_rooms" },
    { to: "/hotel/reservations", label: "Prenotazioni", icon: CalendarCheck, testId: "nav-hotel-reservations", perm: "manage_reservations" },
    { to: "/hotel/planner", label: "Planner Camere", icon: CalendarRange, testId: "nav-hotel-planner", perm: "manage_reservations" },
    { to: "/hotel/calendar", label: "Calendario", icon: CalendarRange, testId: "nav-hotel-calendar", perm: "manage_reservations" },
    { to: "/hotel/checkin", label: "Check-in", icon: LogIn, testId: "nav-hotel-checkin", perm: "manage_checkin" },
    { to: "/hotel/checkout", label: "Check-out", icon: LogOut, testId: "nav-hotel-checkout", perm: "manage_checkout" },
    { to: "/hotel/housekeeping", label: "Housekeeping", icon: Brush, testId: "nav-hotel-housekeeping", perm: "manage_housekeeping" },
    { to: "/hotel/payments", label: "Pagamenti", icon: HotelWalletIcon, testId: "nav-hotel-payments", perm: "manage_payments" },
    { to: "/hotel/reports", label: "Report", icon: FileBarChart, testId: "nav-hotel-reports", perm: "view_reports" },
    { to: "/hotel/guests", label: "Ospiti", icon: UsersIcon, testId: "nav-hotel-guests", perm: "manage_guests" },
    { to: "/hotel/quotes", label: "Preventivi", icon: QuoteIcon, testId: "nav-hotel-quotes", perm: "manage_reservations" },
    { to: "/hotel/schedine", label: "Schedine Alloggiati", icon: ScrollText, testId: "nav-hotel-schedine", perm: "manage_schede" },
    { to: "/hotel/night-audit", label: "Night Audit", icon: MoonIcon, testId: "nav-hotel-night-audit", perm: "manage_night_audit" },
    { to: "/hotel/revenue-management", label: "Revenue Management", icon: TrendingUp, testId: "nav-hotel-revenue-management" },
    { to: "/hotel/distribution/channel-manager", label: "Channel Manager", icon: Radio, testId: "nav-hotel-channel-manager", perm: "distribution_view", badge: "FOUNDATION" },
    { to: "/hotel/audit", label: "Audit trail", icon: HistoryIcon, testId: "nav-hotel-audit", perm: "view_audit" },
    { to: "/hotel/team", label: "Team & Permessi", icon: ShieldIcon, testId: "nav-hotel-team", perm: "manage_team" },
    { to: "/finance/cash-flow", label: "Cash Flow", icon: TrendingUp, testId: "nav-finance-cash-flow", perm: "view_financials" },
    { to: "/finance/intelligence", label: "Intelligence Finanziaria", icon: BarChart3, testId: "nav-finance-intelligence", perm: "view_financials" },
    { to: "/direzione/multi-site", label: "Direzione Multi-Sede", icon: BarChart3, testId: "nav-direzione-multi-site", perm: "view_financials" },
    { to: "/direzione/mbo", label: "MBO / Obiettivi", icon: ShieldIcon, testId: "nav-direzione-mbo", perm: "view_financials" },
  ];
  // RBAC client filter: hide nav entries the role cannot access. Backend
  // returns 403 anyway, but hiding them keeps the sidebar honest and prevents
  // a confusing "Forbidden" landing for menu items the user shouldn't see.
  const visibleLinks = links.filter((l) => !l.perm || (perms && perms.has(l.perm)));
  return (
    <div className="mt-5">
      <div className="label-eyebrow px-3 mb-2 flex items-center gap-1.5" data-testid="hotel-nav-section-label">
        <HotelIcon className="w-3 h-3 text-[#0F766E]" />
        {sectionLabel}
      </div>
      <div className="space-y-0.5">
        {visibleLinks.map((l) => (
          <NavItem key={l.to} {...l} />
        ))}
      </div>
    </div>
  );
}


function RestaurantNav({ t }) {
  const { can } = usePermissions();
  const links = [
    { to: "/restaurant/dashboard", label: t("restaurant_nav.dashboard"), icon: LayoutDashboard, testId: "nav-rest-dashboard" },
    { to: "/restaurant/pos/new", label: t("restaurant_nav.pos"), icon: PlusCircle, testId: "nav-rest-pos" },
    { to: "/restaurant/handheld", label: "Palmare Sala", icon: Smartphone, testId: "nav-rest-handheld", badge: "PWA" },
    { to: "/restaurant/orders", label: t("restaurant_nav.orders"), icon: Receipt, testId: "nav-rest-orders" },
    { to: "/restaurant/kitchen", label: t("restaurant_nav.kitchen"), icon: ChefHat, testId: "nav-rest-kitchen" },
    { to: "/restaurant/tables", label: t("restaurant_nav.tables"), icon: Utensils, testId: "nav-rest-tables" },
    { to: "/restaurant/menu", label: t("restaurant_nav.menu"), icon: BookOpen, testId: "nav-rest-menu" },
    { to: "/restaurant/qr-menu", label: "QR Menu Tavoli", icon: QrCode, testId: "nav-rest-qr-menu" },
    { to: "/restaurant/reservations", label: t("restaurant_nav.reservations"), icon: CalendarCheck, testId: "nav-rest-reservations" },
    { to: "/restaurant/takeaway", label: t("restaurant_nav.takeaway"), icon: ShoppingBag, testId: "nav-rest-takeaway" },
    { to: "/restaurant/customers", label: t("restaurant_nav.customers"), icon: ContactRound, testId: "nav-rest-customers" },
    { to: "/restaurant/payments", label: t("restaurant_nav.payments"), icon: Wallet, testId: "nav-rest-payments" },
  ];
  // Manager Intelligence — visible only if user has any of the daily-close perms
  if (can("close_restaurant_day") || can("view_restaurant_daily_reports")) {
    links.push({
      to: "/restaurant/daily-close",
      label: t("restaurant_nav.daily_close", "Chiusura giornata"),
      icon: ShieldIcon,
      testId: "nav-rest-daily-close",
    });
  }
  return (
    <div className="mt-5">
      <div className="label-eyebrow px-3 mb-2">{t("nav.restaurant_section")}</div>
      <div className="space-y-0.5">
        {links.map((l) => (
          <NavItem key={l.to} {...l} />
        ))}
      </div>
    </div>
  );
}

function EnterpriseNav({ t, tenant }) {
  // Phase 12.1 — Enterprise group dashboard is ENTERPRISE ONLY.
  // Combo Premium MUST NOT see this section anymore.
  const isEnterprise = tenant?.plan_tier === "enterprise";
  if (!isEnterprise) return null;
  const links = [
    { to: "/enterprise/dashboard", label: t("enterprise.nav_dashboard"), icon: Crown, testId: "nav-ent-dashboard" },
    { to: "/enterprise/locations", label: t("enterprise.nav_locations"), icon: Building2, testId: "nav-ent-locations" },
    { to: "/enterprise/benchmark", label: t("enterprise.nav_benchmark"), icon: Activity, testId: "nav-ent-benchmark" },
    { to: "/enterprise/audit-log", label: t("enterprise.nav_audit"), icon: FileSearch, testId: "nav-ent-audit" },
  ];
  return (
    <div className="mt-5">
      <div className="label-eyebrow px-3 mb-2 flex items-center gap-1.5">
        <Crown className="w-3 h-3 text-[#A36B0F]" />
        {t("enterprise.nav_section")}
      </div>
      <div className="space-y-0.5">
        {links.map((l) => (
          <NavItem key={l.to} {...l} />
        ))}
      </div>
    </div>
  );
}

// Iter 80 Fase 6 — Operations Intelligence Center 2.0 sidebar entry
// Decision-grade dashboard for GM/Owner/Hotel Manager. Tenant-scoped.
// Hidden for any role without view_operations_center (hide-not-disable).
function OperationsCenterNav() {
  const { can } = usePermissions();
  if (!can("view_operations_center")) return null;
  return (
    <NavItem
      to="/operations-center"
      label="Operations Center"
      icon={Activity}
      testId="nav-operations-center"
    />
  );
}

// Iter 81 Fase 6.1 — Weekly Executive Alerts config
// Hide-not-disable: require view_executive_alerts (owner/manager/GM/accountant).
function ExecutiveAlertsNav() {
  const { can } = usePermissions();
  if (!can("view_executive_alerts")) return null;
  return (
    <NavItem
      to="/operations/executive-alerts"
      label="Executive Alerts"
      icon={Bell}
      testId="nav-executive-alerts"
    />
  );
}

// Iter 82 Fase 6.2 — Data Activation Center
// Hide-not-disable: requires view_financial_dashboard.
function DataActivationNav() {
  const { can } = usePermissions();
  if (!can("view_financial_dashboard")) return null;
  return (
    <NavItem
      to="/operations/data-activation"
      label="Data Activation"
      icon={Database}
      testId="nav-data-activation"
    />
  );
}

// Iter 85 Fase 7 — Setup Wizard (Guided Onboarding v2)
// Hide-not-disable: requires manage_settings (owner/admin/GM/tenant_admin).
function SetupWizardNav() {
  const { can } = usePermissions();
  if (!can("manage_settings")) return null;
  return (
    <NavItem
      to="/onboarding"
      label="Setup Wizard"
      icon={Rocket}
      testId="nav-onboarding"
    />
  );
}

// Iter 78 Fase 5 — Financial Operations Enterprise sidebar entries
function FinancialPlanningNav() {
  const { can } = usePermissions();
  if (!can("manage_financial_budgets") && !can("view_financial_dashboard") && !can("view_financial_statements")) return null;
  return (
    <NavItem
      to="/finance/planning"
      label="Financial Planning"
      icon={Wallet}
      testId="nav-financial-planning"
    />
  );
}

function ExecutiveFinancialNav() {
  const { can } = usePermissions();
  if (!can("view_financial_dashboard")) return null;
  return (
    <NavItem
      to="/finance/executive-financial"
      label="Exec Financial"
      icon={BarChart3}
      testId="nav-executive-financial"
    />
  );
}

function FinancialStatementsNav() {
  const { can } = usePermissions();
  if (!can("view_financial_statements")) return null;
  return (
    <NavItem
      to="/finance/statements"
      label="Financial Statements"
      icon={FileText}
      testId="nav-financial-statements"
    />
  );
}

// Iter 92.5b — Automazioni finanziarie (events log da autoingest reali).
function FinanceAutomationsNav() {
  const { can } = usePermissions();
  if (!can("view_financial_statements")) return null;
  return (
    <NavItem
      to="/finance/automations"
      label="Automazioni finanziarie"
      icon={Zap}
      testId="nav-finance-automations"
    />
  );
}

// Iter 92.6b — Riconciliazione giornaliera (Night Audit foundation).
function NightAuditReconciliationNav() {
  const { can } = usePermissions();
  if (!can("view_financial_statements")) return null;
  return (
    <NavItem
      to="/hotel/night-audit/reconciliation"
      label="Riconciliazione giornaliera"
      icon={ShieldCheck}
      testId="nav-night-audit-reconciliation"
    />
  );
}

// Iter 85 Fase 7.2 — Period Closures (hybrid lock).
// Visible to roles with view_financial_dashboard; write-actions hidden inside
// the page itself when manage_financial_budgets is missing.
function PeriodClosuresNav() {
  const { can } = usePermissions();
  if (!can("view_financial_dashboard") && !can("manage_financial_budgets")) return null;
  return (
    <NavItem
      to="/finance/periods"
      label="Chiusure di periodo"
      icon={ShieldCheck}
      testId="nav-period-closures"
    />
  );
}

// Iter 86 Fase 8 — Business Intelligence Enterprise.
// Gated on view_financial_dashboard (same SSOT as Executive Financial).
function IntelligenceDashboardNav() {
  const { can } = usePermissions();
  if (!can("view_financial_dashboard")) return null;
  return (
    <NavItem
      to="/bi/dashboard"
      label="Intelligence Dashboard"
      icon={BarChart3}
      testId="nav-bi-dashboard"
    />
  );
}

// Iter 53 Fase 3B B1 — Cash Shift Management (cross-vertical).
// Available to ANY role with cash_shift_view (cashier, reception, manager,
// accountant, owner). Does NOT require executive_financial module.
function CashShiftNav() {
  const { can } = usePermissions();
  if (!can("cash_shift_view")) return null;
  return (
    <NavItem
      to="/finance/cash-shifts"
      label="Cassa per turno"
      icon={Wallet}
      testId="nav-cash-shifts"
    />
  );
}

// Iter 55/56 Fase 3B Compliance Pack — universal, permission-only.
// Visible whenever ANY of city_tax_view, city_tax_export,
// accounting_export_view, accounting_export_generate is granted.
function ComplianceNav() {
  const { can } = usePermissions();
  const ok = can("city_tax_view") || can("city_tax_export")
          || can("accounting_export_view") || can("accounting_export_generate");
  if (!ok) return null;
  return (
    <NavItem
      to="/finance/compliance"
      label="Compliance Finanziaria"
      icon={ShieldCheck}
      testId="nav-financial-compliance"
    />
  );
}

// Iter 76 Fase 3 — Audit Trail Enterprise sidebar entry
// Visible to any role with view_audit_trail (owner/manager/accountant/auditor/admin)
function AuditTrailNav() {
  const { can } = usePermissions();
  if (!can("view_audit_trail")) return null;
  return (
    <NavItem
      to="/audit-trail"
      label="Audit Trail"
      icon={FileSearch}
      testId="nav-audit-trail"
    />
  );
}

// Iter 77 Fase 4 — Custom Roles sidebar entry (owner/admin only)
function CustomRolesNav() {
  const { can } = usePermissions();
  if (!can("manage_custom_roles")) return null;
  return (
    <NavItem
      to="/security/custom-roles"
      label="Ruoli personalizzati"
      icon={ShieldIcon}
      testId="nav-custom-roles"
    />
  );
}

// Iter 77 Fase 4 — Permission Inspector sidebar entry
function PermissionInspectorNav() {
  const { can } = usePermissions();
  if (!can("view_permission_inspector")) return null;
  return (
    <NavItem
      to="/security/permission-inspector"
      label="Permission Inspector"
      icon={FileSearch}
      testId="nav-permission-inspector"
    />
  );
}

// Iter 58 Fase 3B Final — Approval Center sidebar entry
// Visible to anyone who can create OR view approval requests.
function ApprovalCenterNav() {
  const { can } = usePermissions();
  if (!can("approval_request_view") && !can("approval_request_create")) return null;
  return (
    <NavItem
      to="/governance/approvals"
      label="Centro Approvazioni"
      icon={ShieldCheck}
      testId="nav-approval-center"
    />
  );
}

// Iter 59 Fase 3B Final Polish — Accounting Codes sidebar entry
function AccountingCodesNav() {
  const { can } = usePermissions();
  if (!can("accounting_export_view") && !can("accounting_codes_manage")) return null;
  return (
    <NavItem
      to="/finance/accounting-codes"
      label="Piano conti"
      icon={FileText}
      testId="nav-accounting-codes"
    />
  );
}

// Iter 75 Fase 2 (Gap 1) — Combo End-of-Day sidebar entry
// Visible ONLY when the tenant has BOTH hotel_core AND pos modules — mirrors the
// backend module gate so non-combo tenants never see the link.
function ComboEndOfDayNav({ enabledModules }) {
  const { can } = usePermissions();
  if (!can("view_combo_end_of_day")) return null;
  const hasBoth = enabledModules.includes("hotel_core") && enabledModules.includes("pos");
  if (!hasBoth) return null;
  return (
    <div className="mt-5">
      <div className="label-eyebrow px-3 mb-2 flex items-center gap-1.5">
        <Crown className="w-3 h-3 text-[#102A43]" />
        Chiusura combo
      </div>
      <div className="space-y-0.5">
        <NavItem
          to="/combo/end-of-day"
          label="End-of-Day consolidato"
          icon={ShieldIcon}
          testId="nav-combo-end-of-day"
        />
      </div>
    </div>
  );
}

function EconomicsNav({ t, enabledModules }) {
  const has = (m) => enabledModules.includes(m);
  const hasAny = has("inventory") || has("recipes") || has("food_cost");
  if (!hasAny) return null;
  const links = [];
  if (has("inventory")) {
    links.push(
      { to: "/restaurant/inventory", label: t("restaurant_nav.inventory"), icon: Warehouse, testId: "nav-inv-dashboard" },
      { to: "/restaurant/suppliers", label: t("restaurant_nav.suppliers"), icon: Truck, testId: "nav-inv-suppliers" },
      { to: "/restaurant/supplier-intelligence", label: "Confronto prezzi", icon: BarChart3, testId: "nav-inv-supplier-intelligence" },
      { to: "/restaurant/ingredients", label: t("restaurant_nav.ingredients"), icon: Sprout, testId: "nav-inv-ingredients" },
      { to: "/restaurant/movements", label: t("restaurant_nav.movements"), icon: ListChecks, testId: "nav-inv-movements" },
      { to: "/restaurant/lots-expiry", label: "Scadenze e Lotti", icon: CalendarClock, testId: "nav-inv-lots-expiry" },
      { to: "/restaurant/purchase-orders", label: "Acquisti", icon: ShoppingCart, testId: "nav-inv-purchasing" },
      { to: "/restaurant/supplier-invoice-import", label: "Importa fatture fornitori", icon: FileText, testId: "nav-inv-supplier-invoice-import" },
      { to: "/restaurant/physical-inventory", label: "Inventario fisico", icon: ClipboardCheck, testId: "nav-inv-physical" },
      { to: "/restaurant/wine-cellar", label: "Cantina", icon: Wine, testId: "nav-inv-wine-cellar" }
    );
  } else if (has("recipes") || has("food_cost")) {
    // Ingredient catalogue is still useful even without inventory
    links.push({
      to: "/restaurant/ingredients",
      label: t("restaurant_nav.ingredients"),
      icon: Sprout,
      testId: "nav-inv-ingredients",
    });
  }
  if (has("recipes") || has("food_cost")) {
    links.push({ to: "/restaurant/recipes", label: t("restaurant_nav.recipes"), icon: BookOpen, testId: "nav-inv-recipes" });
    links.push({ to: "/restaurant/sub-recipes", label: "Sub-ricette", icon: BookOpen, testId: "nav-inv-sub-recipes" });
  }
  if (has("food_cost")) {
    links.push({ to: "/restaurant/food-cost", label: t("restaurant_nav.food_cost"), icon: TrendingUp, testId: "nav-inv-food-cost" });
    links.push({ to: "/restaurant/food-cost/intelligence", label: "F&B Intelligence", icon: Sparkles, testId: "nav-fnb-intelligence" });
    links.push({ to: "/restaurant/food-cost/categories", label: "Categorie", icon: Tag, testId: "nav-fc-categories" });
    links.push({ to: "/restaurant/fnb/kpi", label: "KPI F&B", icon: Gauge, testId: "nav-fnb-kpi" });
    links.push({ to: "/restaurant/fnb/periods", label: "Chiusura periodo", icon: CalendarCheck2, testId: "nav-fnb-periods" });
    links.push({ to: "/restaurant/break-even", label: "Break-even", icon: Target, testId: "nav-break-even" });
  }
  return (
    <div className="mt-5">
      <div className="label-eyebrow px-3 mb-2">{t("inventory_section")}</div>
      <div className="space-y-0.5">
        {links.map((l) => (
          <NavItem key={l.to} {...l} />
        ))}
      </div>
    </div>
  );
}

function AIControllerNav({ t, user }) {
  // Visibile solo a ruoli manager-level (no staff/viewer)
  const allowed = new Set([
    "platform_owner",
    "platform_admin",
    "restaurant_owner",
    "hotel_owner",
    "restaurant_manager",
    "hotel_manager",
    "enterprise_owner",
    "enterprise_admin",
    "area_manager",
  ]);
  if (!allowed.has(user?.role)) return null;
  return (
    <div className="mt-5">
      <div className="label-eyebrow px-3 mb-2 flex items-center gap-1.5">
        <Brain className="w-3 h-3 text-[#0F766E]" />
        AI Controller
      </div>
      <div className="space-y-0.5">
        <NavItem
          to="/ai-controller"
          label="Controller finanziario"
          icon={Brain}
          testId="nav-ai-controller"
        />
        <NavItem
          to="/ai-controller/profit-recovery"
          label="Profit Recovery"
          icon={WalletIcon}
          testId="nav-profit-recovery"
        />
      </div>
    </div>
  );
}

export default function Sidebar({ onNavigate }) {
  const { t } = useTranslation();
  const { user, tenant, isPlatform } = useAuth();
  const { permissions: topLevelPerms } = usePermissions();
  // Phase 10A: prefer the centralized access matrix as the SSOT.
  // Falls back to legacy `tenant.enabled_modules` if matrix not yet loaded.
  const access = useAccessMatrix();
  const enabledModules = access?.effective_modules?.length
    ? access.effective_modules
    : (tenant?.enabled_modules || []);
  const hasRestaurant =
    enabledModules.includes("restaurant_core") || enabledModules.includes("combo_integration");
  // Phase 12.1 — module-level gating helpers driven by the access matrix.
  // Combo Premium / Hotel Pro / Restaurant Pro never have these flags ⇒ the
  // corresponding sidebar sections are hidden, not just visually disabled.
  const hasExecutiveFinancial = enabledModules.includes("executive_financial");
  const hasIntelligenceBi = enabledModules.includes("intelligence_bi");
  const hasAuditTrailEnt = enabledModules.includes("audit_trail_enterprise");
  // Iter 53 Fase 3B B1 — Cash Shift uses permission-only gating (no module).
  // The section appears whenever the user has cash_shift_view OR any
  // executive_financial entitlement, ensuring it works on Combo Premium too.
  const hasCashShiftAccess = (topLevelPerms || []).includes?.("cash_shift_view") ?? false;
  // Iter 55/56 Fase 3B Compliance Pack — visible if ANY of the compliance perms.
  const hasComplianceAccess = (topLevelPerms || []).some?.((p) =>
    p === "city_tax_view" || p === "city_tax_export" ||
    p === "accounting_export_view" || p === "accounting_export_generate",
  ) ?? false;
  // Iter 58 Fase 3B Final — Approval Workflow Center.
  const hasGovernanceAccess = (topLevelPerms || []).some?.((p) =>
    p === "approval_request_view" || p === "approval_request_create" ||
    p === "approval_request_approve" || p === "approval_threshold_manage",
  ) ?? false;
  // Iter 59 Fase 3B Final Polish — Accounting Codes (chart of accounts admin)
  const hasAccountingCodesAccess = (topLevelPerms || []).some?.((p) =>
    p === "accounting_export_view" || p === "accounting_codes_manage",
  ) ?? false;

  const platformLinks = useMemo(
    () => [
      // Master Control Tower — homepage SaaS operations
      { to: "/platform", label: "Control Tower", icon: LayoutDashboard, testId: "nav-control-tower" },
      { to: "/platform/command-center", label: "Command Center", icon: Crown, testId: "nav-occ" },  // Iter 61 — OCC
      { to: "/tenants", label: t("nav.tenants"), icon: Building2, testId: "nav-tenants" },
      { to: "/users", label: t("nav.users"), icon: Users, testId: "nav-users" },
      { to: "/roles", label: t("nav.roles"), icon: ShieldCheck, testId: "nav-roles" },
      { to: "/modules", label: t("nav.modules"), icon: Boxes, testId: "nav-modules" },
      { to: "/platform/integrations", label: t("nav.integrations"), icon: Plug, testId: "nav-platform-integrations" },
      { to: "/platform/trials", label: "Trial Management", icon: Clock, testId: "nav-platform-trials" },
      // Iter 83 — Platform Activation Requests inbox
      { to: "/platform/activation-requests", label: "Richieste attivazione", icon: Inbox, testId: "nav-platform-activation-requests" },
      // Iter 91B.9 — Owner Console: Communications + SMS Packs + Secrets
      { to: "/platform/communications", label: "Console comunicazioni", icon: Mail, testId: "nav-platform-communications" },
      { to: "/platform/whatsapp-providers", label: "WhatsApp Providers", icon: MessageSquare, testId: "nav-platform-whatsapp-providers" },
      { to: "/platform/sms-packs", label: "Pacchetti SMS", icon: MessageSquare, testId: "nav-platform-sms-packs" },
      { to: "/platform/secrets", label: "Secrets & ENV", icon: KeyRound, testId: "nav-platform-secrets" },
      { to: "/platform/operations-log", label: "Registro operativo", icon: ScrollText, testId: "nav-platform-operations-log" },
      // Iter 76 — Platform Owner Billing (visible to owner + admin; write
      // restricted server-side to platform_owner role only).
      { to: "/platform/billing", label: "Fatturazione Piattaforma", icon: Receipt, testId: "nav-platform-billing" },
      { to: "/activity", label: t("nav.activity"), icon: ScrollText, testId: "nav-activity" },
      { to: "/admin/legal", label: t("nav.legal_admin"), icon: FileText, testId: "nav-legal-admin" },
      { to: "/settings", label: t("nav.settings"), icon: Settings, testId: "nav-settings" },
      { to: "/help", label: "Centro assistenza", icon: LifeBuoy, testId: "nav-help" },
    ],
    [t]
  );

  const tenantLinks = useMemo(
    () => [
      { to: "/dashboard", label: t("nav.overview"), icon: LayoutDashboard, testId: "nav-overview" },
      { to: "/users", label: t("nav.team"), icon: Users, testId: "nav-users" },
      { to: "/team/permissions", label: "Ruoli e permessi", icon: ShieldCheck, testId: "nav-permissions", requires: "manage_team" },
      { to: "/billing", label: t("nav.billing"), icon: CreditCard, testId: "nav-billing" },
      { to: "/billing/documents", label: t("nav.legal_docs"), icon: FileText, testId: "nav-legal-docs" },
      // Iter 67 + Iter 68 — Documenti fiscali / Provider fiscale SDI (predisposti)
      { to: "/settings/billing-documents", label: "Documenti fiscali", icon: FileSpreadsheet, testId: "nav-tenant-billing-documents", requires: "billing_documents_view" },
      // Iter 92.8 — Profilo fiscale struttura (dati cedente/prestatore)
      { to: "/settings/fiscal-profile", label: "Profilo fiscale struttura", icon: ShieldCheck, testId: "nav-tenant-fiscal-profile", requires: "billing_documents_view" },
      // Iter 73 — Guest Communication Hub (email pre-arrival foundation)
      { to: "/settings/guest-communications", label: "Comunicazioni ospiti", icon: Mail, testId: "nav-tenant-guest-communications", requires: "guest_communications_view" },
      // Iter 74 — Distribution & Calendar foundation (canali OTA + iCal + Google Calendar)
      { to: "/settings/distribution", label: "Canali & Calendari", icon: CalendarCheck2, testId: "nav-tenant-distribution", requires: "distribution_view" },
      // Channel Manager Bridge — crosslink from tenant settings section (nav-tenant scope)
      { to: "/hotel/distribution/channel-manager", label: "Channel Manager", icon: Radio, testId: "nav-tenant-channel-manager", requires: "distribution_view", badge: "FOUNDATION" },
      // Iter 66 — Stampanti & dispositivi (RT, comande, KDS, cassetti)
      { to: "/settings/printers", label: "Stampanti e dispositivi", icon: Printer, testId: "nav-tenant-printers", requires: "manage_settings" },
      { to: "/activity", label: t("nav.activity"), icon: ScrollText, testId: "nav-activity" },
      { to: "/settings", label: t("nav.settings"), icon: Settings, testId: "nav-settings" },
      // Iter 78 — Setup Guide / Onboarding Center (tenant-scoped guidance)
      { to: "/settings/setup-guide", label: "Guida configurazione", icon: ShieldCheck, testId: "nav-tenant-setup-guide" },
      // Legal sales-readiness — legal docs + acceptance gate
      { to: "/settings/legal-documents", label: "Documenti legali", icon: ShieldCheck, testId: "nav-tenant-legal-documents" },
      // Iter 79 — Help / Forms training (documentation, open to all tenants)
      { to: "/help/forms", label: "Guida alla compilazione", icon: BookOpen, testId: "nav-help-forms" },
      { to: "/settings/integrations", label: "Integrazioni e Provider", icon: Plug, testId: "nav-tenant-integrations" },
      { to: "/help", label: "Centro assistenza", icon: LifeBuoy, testId: "nav-help" },
    ],
    [t]
  );

  const allLinks = isPlatform ? platformLinks : tenantLinks;
  // Iter 73 — filter links by `requires` permission. usePermissions() returns
  // a Set under `permissions`, so use .has() for membership.
  const links = allLinks.filter(
    (l) => !l.requires || (topLevelPerms && typeof topLevelPerms.has === "function" && topLevelPerms.has(l.requires))
  );

  return (
    <aside
      className="cx-sidebar-root w-64 shrink-0 h-full border-r border-[#E2E8F0] bg-white flex flex-col"
      data-testid="sidebar"
    >
      <div className="px-5 py-5 border-b border-[#E2E8F0]">
        <div className="flex items-center gap-3">
          <Logo variant="icon" size="md" className="shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="font-heading font-bold text-[14px] tracking-tight text-[#0F172A] leading-none truncate" translate="no">
              CostControlX
            </span>
            <span className="text-[11px] text-[#64748B] mt-1 truncate">
              {isPlatform
                ? t("sidebar.platform_console")
                : tenant?.business_name || t("sidebar.workspace_default")}
            </span>
          </div>
        </div>
        {!isPlatform && tenant && (
          <div className="mt-3 text-[11px] text-[#64748B]">
            <span className="pill bg-[#E0F2F1] text-[#0F766E] uppercase tracking-widest text-[10px]" translate="no">
              {planLabel(t, tenant.plan_id)}
            </span>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4" onClick={onNavigate}>
        <div className="label-eyebrow px-3 mb-2">
          {isPlatform ? t("nav.platform_section") : t("nav.workspace_section")}
        </div>
        <div className="space-y-0.5">
          {links.map((l) => (
            <NavItem key={l.to} {...l} />
          ))}
        </div>

        {!isPlatform && hasRestaurant && <RestaurantNav t={t} />}
        {!isPlatform && <HotelNav t={t} enabledModules={enabledModules} tenant={tenant} />}
        {!isPlatform && <EconomicsNav t={t} enabledModules={enabledModules} />}
        {!isPlatform && <EnterpriseNav t={t} tenant={tenant} />}
        {!isPlatform && <ComboEndOfDayNav enabledModules={enabledModules} />}
        {!isPlatform && hasIntelligenceBi && (
          <div className="mt-5">
            <div className="label-eyebrow px-3 mb-2 flex items-center gap-1.5">
              <Activity className="w-3 h-3 text-[#102A43]" />
              Decision Hub
            </div>
            <div className="space-y-0.5">
              <OperationsCenterNav />
              <ExecutiveAlertsNav />
              <DataActivationNav />
              <SetupWizardNav />
            </div>
          </div>
        )}
        {!isPlatform && !hasIntelligenceBi && (
          // SetupWizard is operative for every plan (onboarding) — keep it
          // visible even when the BI bundle is not entitled.
          <div className="mt-5">
            <div className="label-eyebrow px-3 mb-2 flex items-center gap-1.5">
              <Activity className="w-3 h-3 text-[#102A43]" />
              Setup
            </div>
            <div className="space-y-0.5">
              <SetupWizardNav />
            </div>
          </div>
        )}
        {!isPlatform && (hasExecutiveFinancial || hasCashShiftAccess || hasComplianceAccess) && (
          <div className="mt-5">
            <div className="label-eyebrow px-3 mb-2 flex items-center gap-1.5">
              <BarChart3 className="w-3 h-3 text-[#0F766E]" />
              Financial Operations
            </div>
            <div className="space-y-0.5">
              <CashShiftNav />{/* Iter 53 Fase 3B B1 — universal, permission-only */}
              <ComplianceNav />{/* Iter 55/56 Fase 3B Compliance Pack */}
              <FinancialPlanningNav />
              <ExecutiveFinancialNav />
              <FinancialStatementsNav />
              <FinanceAutomationsNav />{/* Iter 92.5b */}
              <NightAuditReconciliationNav />{/* Iter 92.6b */}
              <PeriodClosuresNav />{/* Iter 85 Fase 7.2 */}
              <IntelligenceDashboardNav />{/* Iter 86 Fase 8 */}
            </div>
          </div>
        )}
        {!isPlatform && (hasAuditTrailEnt || hasGovernanceAccess || hasAccountingCodesAccess) && (
          <div className="mt-5">
            <div className="label-eyebrow px-3 mb-2 flex items-center gap-1.5">
              <FileSearch className="w-3 h-3 text-[#7C3AED]" />
              Compliance & Security
            </div>
            <div className="space-y-0.5">
              <AuditTrailNav />
              <PermissionInspectorNav />
              <CustomRolesNav />
              <ApprovalCenterNav />{/* Iter 58 Fase 3B Final — Approval Workflow */}
              <AccountingCodesNav />{/* Iter 59 Fase 3B Final Polish — Accounting Codes */}
            </div>
          </div>
        )}
        <AIControllerNav t={t} user={user} />

        {!isPlatform &&
          MODULE_NAV_GROUPS.map((g) => (
            <ModuleGroup
              key={g.id}
              group={{
                ...g,
                // Hide modules already shown in the active Restaurant section,
                // and hide modules already promoted to the Economics section.
                items: g.items.filter(
                  (id) =>
                    !(hasRestaurant && ["pos", "reservations", "takeaway", "payments"].includes(id))
                    && !["inventory", "recipes", "food_cost"].includes(id)
                ),
              }}
              enabledModules={enabledModules}
              tenantPlanId={tenant?.plan_id}
              t={t}
              perms={topLevelPerms}
            />
          ))}
      </nav>

      <div className="p-3 border-t border-[#E2E8F0]">
        <div className="px-2 py-1 text-[11px] text-[#475569] flex items-center justify-between gap-2">
          <span className="shrink-0">{t("sidebar.signed_in_as")}</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#102A43] truncate text-right">
            {roleLabel(t, user?.role)}
          </span>
        </div>
        <div
          className="mt-2 pt-2 border-t border-dashed border-[#E2E8F0] flex items-center gap-2 px-2"
          data-testid="sidebar-footer-brand"
        >
          <span className="grid place-items-center w-5 h-5 rounded bg-[#102A43] text-white">
            <ChefHat className="w-3 h-3" />
          </span>
          <div className="flex flex-col leading-tight min-w-0">
            <span className="text-[10px] font-semibold text-[#0f172a] tracking-tight">
              CostControlX
            </span>
            <span className="text-[9px] text-[#475569] uppercase tracking-widest truncate">
              SaaS Hospitality Suite
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
}
